import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useEffect,
} from 'react';
import {
    defaultFilterTypes,
    sortDirections,
} from '../../types/lists/listTypes';
import { buildEndPoint, mapFiltersToOdata } from '../../utils/odataEndPoint';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import api from '../../services/api';
import useLanguage from '../../customHooks/useLanguage';
import {
    ConnetionRequestSource,
    IConnectionRequestState,
    IFilterOptionState,
    IFiltersState,
} from '../../types/zinantis/connectionRequests/connectionRequestTypes';
import { IZinantisConnectionRequest } from '../../types/entities/IZinantisConnectionRequest';
import useTimeZone from '../../customHooks/useTimeZone';
import { useNotification } from '../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../utils/commonVariables/messages';

interface ChannelManagersContextType {
    filters: IFiltersState;
    handleFilterChange: (
        filterKey: string,
        value: string | number | null | IFilterOptionState | Date
    ) => void;
    handleSearch: (pageNumber?: number) => Promise<void>;
    resetFilters: () => void;
    items: IConnectionRequestState[];
    sortedBy: string;
    sortDir: sortDirections;
    sortByField: (field: string) => void;
    currentResults: number;
    totalResults: number;
    isLoading: boolean;
    handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    handleLoading: (loading: boolean) => void;
    requestedBy: ConnetionRequestSource;
    isCollapsed: boolean;
    toggleCollapse: () => void;
}

const ContextConnectionRequests = createContext<
    ChannelManagersContextType | undefined
>(undefined);

export const ProviderConnectionRequests = ({
    children,
    requestedBy,
}: {
    children: ReactNode;
    requestedBy: ConnetionRequestSource;
}) => {
    const language = useLanguage();
    const { setErrorMessage } = useNotification();
    const timeZone = useTimeZone();
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [sortDir, setSortDir] = useState<sortDirections>(sortDirections.DESC);
    const [sortedBy, setSortedBy] = useState('DateLastUpdated');
    const resultsPerPage = 40;
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    const [items, setItems] = useState<IConnectionRequestState[]>([]);

    const FILTERS_INITIAL_STATE: IFiltersState = {
        IdZinantisConnectionRequestStatusType: {
            value: null,
            filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
            filterKey:
                'ZinantisConnectionRequestStatus/IdZinantisConnectionRequestStatusType',
        },
        IdAgency: {
            value: null,
            filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
            filterKey: 'IdAgency',
        },
        RequestedFrom: {
            filterType: defaultFilterTypes.DEFAULT_DATE_TIME_FROM_FILTER_TYPE,
            filterKey: 'RequestDate',
            value: null,
            timeZone: timeZone,
        },
        RequestedTo: {
            filterType: defaultFilterTypes.DEFAULT_DATE_TIME_TO_FILTER_TYPE,
            filterKey: 'RequestDate',
            value: null,
            timeZone: timeZone,
        },
    };

    const [filters, setFilters] = useState(FILTERS_INITIAL_STATE);

    const [isCollapsed, setIsCollapsed] = useState(false);
    const toggleCollapse = () => {
        setIsCollapsed(prev => !prev);
    };

    const handleLoading = (loading: boolean) => {
        setIsLoading(loading);
    };

    const handleFilterChange = (
        filterKey: string,
        value: string | number | null | IFilterOptionState | Date
    ) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterKey]: {
                ...prevFilters[filterKey as keyof IFiltersState],
                value,
            },
        }));
    };

    const resetFilters = () => {
        setFilters(FILTERS_INITIAL_STATE);
        getConnectionRequests(1, FILTERS_INITIAL_STATE);
    };

    const getConnectionRequests = async (
        pageNumber = 1,
        filtersState = filters
    ) => {
        setPage(pageNumber);
        if (pageNumber === 1) setItems([]);
        const filtersToOdata = mapFiltersToOdata(filtersState);
        const endPoint = buildEndPoint(
            {
                entityName: readOnlyEntities.ZinantisConnectionRequest,
                query: {
                    select: [
                        'Id',
                        'ZinantisConnectionRequestStatus/ZinantisConnectionRequestStatusType/Key',
                        'ZinantisConnectionRequestStatus/ZinantisConnectionRequestStatusType/LocalizedCaption',
                        'ZinantisConnectionRequestStatus/ZinantisConnectionRequestStatusType/Caption',
                        'ZinantisConnectionRequestStatus/ZinantisConnectionRequestStatusType/Name',
                        'ZinantisConnectionRequestStatus/ZinantisConnectionRequestStatusType/Color',
                        'IdAgency',
                        'Agency/Name',
                        'Agency/LogoFile/VirtualPath',
                        'RequestDate',
                        'DateLastUpdated',
                        'ResolutionDate',
                        'Agency/LogoFile/VirtualPath',
                        'RequestedBy',
                    ],
                    expand: [
                        `${readOnlyEntities.ZinantisConnectionRequestStatus}/${readOnlyEntities.ZinantisConnectionRequestStatusType}`,
                        readOnlyEntities.Agency,
                        `${readOnlyEntities.Agency}/LogoFile`,
                    ],
                    filter: [
                        `RequestedBy eq ${requestedBy}`,
                        ...filtersToOdata,
                    ],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: resultsPerPage,
                },
            },
            pageNumber
        );
        setIsLoading(true);
        try {
            const { data } = await api.get(endPoint);
            const { value: _items } = data;
            const _totalResults = parseInt(data['odata.count']);

            const newItems: IConnectionRequestState[] = _items.map(
                (item: IZinantisConnectionRequest) => ({
                    Id: item.Id,
                    IdAgency: item.IdAgency,
                    Agency: item.Agency.Name,
                    IdStatus:
                        item.ZinantisConnectionRequestStatus
                            ?.IdZinantisConnectionRequestStatusType,
                    StatusKey:
                        item.ZinantisConnectionRequestStatus
                            ?.ZinantisConnectionRequestStatusType?.Key,
                    Status:
                        item.ZinantisConnectionRequestStatus
                            ?.ZinantisConnectionRequestStatusType
                            ?.LocalizedCaption ||
                        item.ZinantisConnectionRequestStatus
                            ?.ZinantisConnectionRequestStatusType?.Caption ||
                        item.ZinantisConnectionRequestStatus
                            ?.ZinantisConnectionRequestStatusType?.Name,
                    StatusColor:
                        item.ZinantisConnectionRequestStatus
                            ?.ZinantisConnectionRequestStatusType?.Color,
                    RequestDate: item.RequestDate,
                    ResolutionDate: item.ResolutionDate,
                    DateLastUpdated: item.DateLastUpdated,
                    RequestedBy: item.RequestedBy,
                    AgencyLogo: item.Agency.LogoFile?.VirtualPath || '',
                })
            );

            if (pageNumber === 1) {
                setItems(newItems);
                setTotalResults(_totalResults);
            } else {
                setItems(prev => [...prev, ...newItems]);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getConnectionRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, sortedBy, sortDir]);

    const handleSearch = async (pageNumber = 1) => {
        await getConnectionRequests(pageNumber);
    };

    const handleSortByField = (field: string) => {
        if (field === sortedBy) {
            setSortDir(prevSortDir =>
                prevSortDir === sortDirections.ASC
                    ? sortDirections.DESC
                    : sortDirections.ASC
            );
        } else {
            setSortedBy(field);
            setSortDir(sortDirections.ASC);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isOnBottom = scrollTop > scrollHeight - clientHeight - 1;
        if (isOnBottom && !isLoading && page < totalPages) {
            const nextPage = page + 1;
            setPage(nextPage);
            getConnectionRequests(nextPage);
        }
    };

    return (
        <ContextConnectionRequests.Provider
            value={{
                filters,
                handleFilterChange,
                items,
                handleSearch,
                resetFilters,
                currentResults: items.length,
                totalResults,
                isLoading,
                sortedBy,
                sortDir,
                sortByField: handleSortByField,
                handleScroll,
                handleLoading,
                requestedBy,
                isCollapsed,
                toggleCollapse,
            }}
        >
            {children}
        </ContextConnectionRequests.Provider>
    );
};

export const useConnectionRequests = () => {
    const context = useContext(ContextConnectionRequests);
    if (context === undefined) {
        throw new Error(
            'useConnectionRequests must be used within a ProviderConnectionRequests'
        );
    }
    return context;
};
